/**
 * Offline-first ICS Form Service
 *
 * Handles creating, storing, and syncing ICS forms when offline.
 * Uses IndexedDB (Dexie) for local storage and syncs to the server when online.
 *
 * This is DIFFERENT from form-draft.service.ts (auto-save for preventing work loss).
 * This service provides full offline form creation, viewing, and sync.
 */

import { db, OfflineForm, CachedFormTemplate } from './db';
import { originOnly, mustDeferSend, sendTenantOf } from './client-id';
import { networkService } from '@/services/network.service';
import { API_URL } from '@/services/api';
import { clientModeHeaders } from '@/services/client-mode';
import { useAuthStore } from '@/store/auth.store';
import { useTenantStore } from '@/store/tenant.store';

function generateOfflineId(): string {
  return `offline_${crypto.randomUUID()}`;
}

export interface FormInput {
  template_id: string;
  template_name: string;
  incident_id?: string | null;
  title: string;
  form_data?: Record<string, unknown>;
  status?: 'draft' | 'pending_signature' | 'approved';
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

/**
 * Build standard auth headers for API requests.
 */
function getAuthHeaders(pin?: { tenantId: string }): Record<string, string> {
  const token = useAuthStore.getState().token;
  if (!token) throw new Error('Not authenticated');
  // A stored form is sent under its own tenant (client-id.ts sendTenantOf), never re-read at send time.
  const tenantId = pin?.tenantId || useTenantStore.getState().activeTenant?.id;
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Tenant-ID': tenantId || '',
    // Field-mode form sync: exempt from the console idle timeout (client-mode.ts).
    ...clientModeHeaders(),
  };
}

class FormOfflineService {
  /**
   * Cache server forms to IndexedDB for offline access.
   */
  async cacheFromServer(
    forms: Array<{
      id: string;
      template_id: string;
      title: string;
      incident_id: string | null;
      tenant_id: string;
      form_data: Record<string, unknown>;
      status: string;
      created_at: string;
      updated_at: string;
      template?: { name?: string; code?: string };
    }>
  ): Promise<number> {
    let cached = 0;

    for (const serverForm of forms) {
      // Check if we already have this form locally with pending changes
      const existing = await db.offlineForms
        .where('id')
        .equals(serverForm.id)
        .first();

      if (existing && existing.is_synced === 0) {
        // Local has unsaved changes -- preserve local version
        continue;
      }

      const templateName =
        serverForm.template?.name ||
        serverForm.template?.code ||
        'Unknown Template';

      const record: OfflineForm = {
        id: serverForm.id,
        offline_id: serverForm.id, // Server forms use server ID as offline_id
        template_id: serverForm.template_id,
        template_name: templateName,
        incident_id: serverForm.incident_id,
        tenant_id: serverForm.tenant_id,
        title: serverForm.title,
        form_data: serverForm.form_data || {},
        status: serverForm.status as OfflineForm['status'],
        is_synced: 1,
        created_at: serverForm.created_at,
        updated_at: serverForm.updated_at,
        synced_at: new Date().toISOString(),
      };

      if (existing) {
        // Update existing record
        await db.offlineForms.update(existing.localId!, record);
      } else {
        await db.offlineForms.add(record);
      }
      cached++;
    }

    return cached;
  }

  /**
   * Get all forms, optionally filtered by incident.
   */
  async getAll(incidentId?: string | null): Promise<OfflineForm[]> {
    let results: OfflineForm[];

    if (incidentId) {
      results = await db.offlineForms
        .where('incident_id')
        .equals(incidentId)
        .toArray();
    } else {
      results = await db.offlineForms.toArray();
    }

    // Sort by updated_at descending
    return results.sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );
  }

  /**
   * Get a single form by local ID.
   */
  async getById(localId: number): Promise<OfflineForm | undefined> {
    return db.offlineForms.get(localId);
  }

  /**
   * Get a single form by server UUID.
   */
  async getByServerId(id: string): Promise<OfflineForm | undefined> {
    return db.offlineForms.where('id').equals(id).first();
  }

  /**
   * Create a new offline form draft.
   */
  async create(
    tenantId: string,
    input: FormInput
  ): Promise<OfflineForm> {
    const offlineId = generateOfflineId();
    const now = new Date().toISOString();

    const form: OfflineForm = {
      offline_id: offlineId,
      template_id: input.template_id,
      template_name: input.template_name,
      incident_id: input.incident_id || null,
      tenant_id: tenantId,
      title: input.title,
      form_data: input.form_data || {},
      status: input.status || 'draft',
      is_synced: 0,
      created_at: now,
      updated_at: now,
      ...originOnly(tenantId),
    };

    const localId = await db.offlineForms.add(form);
    form.localId = localId;

    // Try to sync immediately if online
    if (networkService.isOnline()) {
      await this.syncForm(localId);
    }

    return form;
  }

  /**
   * Update form data offline.
   */
  async update(
    localId: number,
    updates: Partial<Pick<OfflineForm, 'title' | 'form_data' | 'status'>>
  ): Promise<OfflineForm | null> {
    const existing = await db.offlineForms.get(localId);
    if (!existing) return null;

    const updatedFields: Partial<OfflineForm> = {
      ...updates,
      updated_at: new Date().toISOString(),
      is_synced: 0,
    };

    await db.offlineForms.update(localId, updatedFields);

    // Try to sync immediately if online
    if (networkService.isOnline()) {
      await this.syncForm(localId);
    }

    return { ...existing, ...updatedFields } as OfflineForm;
  }

  /**
   * Sync a single form to the server.
   */
  async syncForm(localId: number): Promise<boolean> {
    const form = await db.offlineForms.get(localId);
    if (!form || form.is_synced === 1) return true;
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(form)) return false;

    try {
      const headers = getAuthHeaders(sendTenantOf(form));

      if (!form.id || form.id === form.offline_id) {
        // New form -- POST to create on server
        const res = await fetch(`${API_URL}/api/forms`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            template_id: form.template_id,
            incident_id: form.incident_id || undefined,
            title: form.title,
            form_data: form.form_data,
            status: form.status,
          }),
        });

        if (!res.ok) {
          const errBody = await res.text();
          throw new Error(`Server returned ${res.status}: ${errBody}`);
        }

        const data = await res.json();
        const serverId = data.data?.id || data.id;

        await db.offlineForms.update(localId, {
          id: serverId,
          is_synced: 1,
          synced_at: new Date().toISOString(),
          last_error: undefined,
        });
      } else {
        // Existing form -- PATCH to update on server
        const res = await fetch(`${API_URL}/api/forms/${form.id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({
            title: form.title,
            form_data: form.form_data,
            status: form.status,
          }),
        });

        if (!res.ok) {
          const errBody = await res.text();
          throw new Error(`Server returned ${res.status}: ${errBody}`);
        }

        await db.offlineForms.update(localId, {
          is_synced: 1,
          synced_at: new Date().toISOString(),
          last_error: undefined,
        });
      }

      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Sync failed';
      console.error('[FormOffline] Sync failed:', error);

      await db.offlineForms.update(localId, {
        last_error: message,
      });

      return false;
    }
  }

  /**
   * Sync all unsynced forms to the server.
   */
  async syncAll(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    const pending = await db.offlineForms
      .where('is_synced')
      .equals(0)
      .toArray();

    for (const form of pending) {
      if (form.localId == null) continue;
      // Another user's (or another tenant's) form is never synced under this session.
      if (mustDeferSend(form)) continue;

      const success = await this.syncForm(form.localId);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync form "${form.title}"`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Get count of unsynced forms.
   */
  async getUnsyncedCount(): Promise<number> {
    return db.offlineForms.where('is_synced').equals(0).count();
  }

  /**
   * Cache form templates for offline template selection.
   */
  async cacheTemplates(
    templates: Array<{
      id: string;
      tenant_id: string | null;
      code: string;
      name: string;
      description: string | null;
      category: string | null;
      field_schema: Record<string, unknown>;
      is_active: boolean;
      sort_order: number;
    }>
  ): Promise<number> {
    const now = new Date().toISOString();
    const records: CachedFormTemplate[] = templates.map((t) => ({
      id: t.id,
      tenant_id: t.tenant_id,
      code: t.code,
      name: t.name,
      description: t.description,
      category: t.category,
      field_schema: t.field_schema as Record<string, unknown>,
      is_active: t.is_active,
      sort_order: t.sort_order,
      cached_at: now,
    }));

    // Atomic clear+add to prevent empty cache if bulkAdd fails
    await db.transaction('rw', db.cachedFormTemplates, async () => {
      await db.cachedFormTemplates.clear();
      await db.cachedFormTemplates.bulkAdd(records);
    });
    return records.length;
  }

  /**
   * Retrieve cached templates for offline use.
   */
  async getCachedTemplates(): Promise<CachedFormTemplate[]> {
    return db.cachedFormTemplates
      .filter((t) => t.is_active)
      .sortBy('sort_order');
  }

  /**
   * Clear all cached form data.
   */
  async clearCache(): Promise<void> {
    await db.offlineForms.clear();
    await db.cachedFormTemplates.clear();
  }

  /**
   * Clear only synced forms (keep pending).
   */
  async clearSyncedCache(): Promise<void> {
    await db.offlineForms.where('is_synced').equals(1).delete();
  }
}

export const formOfflineService = new FormOfflineService();
